/**
 * Local, typing/logic-only re-declaration of `apps/desktop`'s own
 * `lib/timer/countdown-color-tiers.ts` — same convention this app
 * already follows for its wire-shape types (`use-live-extended-display-
 * data.ts`'s own doc comment): each app owns an independent copy rather
 * than importing the other's `apps/desktop`-scoped module.
 */

export type CountdownColorZone = "normal" | "warning" | "critical";

const MIN_5 = 5 * 60_000;
const MIN_10 = 10 * 60_000;
const MIN_20 = 20 * 60_000;

/**
 * `durationMs` is the item's own total duration (picks the tier);
 * `remainingMs` is compared against that tier's own yellow/red
 * thresholds. An item under 5 minutes falls back to tier 1's threshold
 * (the smallest tier's rule extended downward) rather than having no
 * color behavior at all.
 */
export function resolveCountdownZone(
  durationMs: number,
  remainingMs: number,
  tiers: {
    readonly countdownTier1RedAtMs: number;
    readonly countdownTier2RedAtMs: number;
    readonly countdownTier3YellowAtMs: number;
    readonly countdownTier3RedAtMs: number;
    readonly countdownTier4YellowAtMs: number;
    readonly countdownTier4RedAtMs: number;
  },
): CountdownColorZone {
  if (durationMs < MIN_5) {
    return remainingMs <= tiers.countdownTier1RedAtMs ? "critical" : "normal";
  }
  if (durationMs < MIN_10) {
    return remainingMs <= tiers.countdownTier2RedAtMs ? "critical" : "normal";
  }
  if (durationMs < MIN_20) {
    if (remainingMs <= tiers.countdownTier3RedAtMs) return "critical";
    if (remainingMs <= tiers.countdownTier3YellowAtMs) return "warning";
    return "normal";
  }
  if (remainingMs <= tiers.countdownTier4RedAtMs) return "critical";
  if (remainingMs <= tiers.countdownTier4YellowAtMs) return "warning";
  return "normal";
}
